import { appStore } from "$app/store.svelte"
import { ROLE_RELATIONSHIPS, SUBSCRIPTION_ROLES_LIST } from "$app/constants"

type ProductRoleAttrsType = {
  id: ProductRoleType
  min: number
  max: number
  title: string
}

export const SIGNUP_PRODUCT_ROLES: ProductRoleAttrsType[] = [
  {
    id: "full_access",
    title: "Full Access",
    min: 1,
    max: 50
  },
  {
    id: "team_member",
    title: "Team Member",
    min: 0,
    max: 50
  },
  {
    id: "recognition_member",
    title: "Recognition Member",
    min: 0,
    max: 50
  }
] as const

class SignupStore {
  subscription = $state<SubscriptionType>({} as SubscriptionType)
  products: any[] = $state([])
  billingInterval: "year" | "month" | "not_applicable" = $state("year")
  member_roles: (ProductRoleAttrsType & { count: number })[] = $state([
    ...SIGNUP_PRODUCT_ROLES.map((role) => ({ ...role, count: role.min }))
  ])
  coupon = $state({
    id: "",
    percent_off: 0,
    valid: true,
    name: ""
  } as Partial<StripeCouponType>)
  selectedPlan: any = $state({})
  member_count = $state(0)
  stripe_publish_key = $state("")

  stripe_data = $state<StripeDataType>({
    subscription: {
      status: "",
      id: "",
      application: "",
      billing_cycle_anchor: 0,
      created: 0,
      current_period_end: 0,
      current_period_start: 0,
      customer: "",
      discount: { coupon: {} as StripeCouponType },
      trial_end: 0,
      trial_start: 0
    },
    payment_method: {} as StripePaymentMethodType,
    publish_key: "",
    session_id: "",
    stripe_invoices: []
  })

  get standardPlan() {
    return this.products.find((p) => p.id === "standard")
  }

  totalYear() {
    return this.member_roles.reduce((tot, role) => {
      return tot + this.standardPlan.pricing[role.id].annualTotal * role.count
    }, 0)
  }

  totalMonth() {
    return this.member_roles.reduce((tot, role) => {
      return tot + this.standardPlan.pricing[role.id].month * role.count
    }, 0)
  }

  get totalAmount() {
    if (this.billingInterval === "year") return this.totalYear()

    if (this.billingInterval === "month") return this.totalMonth()

    return 0
  }

  discountYear() {
    return (this.totalYear() * (this.coupon.percent_off || 0)) / 100
  }

  discountMonth() {
    return (this.totalMonth() * (this.coupon.percent_off || 0)) / 100
  }

  get discount() {
    if (this.billingInterval === "year") {
      return this.discountYear()
    }

    if (this.billingInterval === "month") {
      return this.discountMonth()
    }

    return 0
  }

  get total() {
    if (this.billingInterval === "year") {
      return this.totalYear() - this.discountYear()
    }

    if (this.billingInterval === "month") {
      return this.totalMonth() - this.discountMonth()
    }
  }

  get stripe_price() {
    if (this.selectedPlan) {
      return this.selectedPlan[`price_${this.billingInterval}`]
    }
  }

  signupCount(role: ProductRoleType): number {
    return this.member_roles.find((m) => m.id === role)?.count || 0
  }

  signupCountTotal(): number {
    return this.member_roles.reduce((tot, role) => {
      return tot + role.count
    }, 0)
  }

  membersCount(role: ProductRoleType): number {
    return ROLE_RELATIONSHIPS[role].reduce((tot: number, rol: RoleType) => {
      return tot + appStore.users.filter((u) => u.role === rol).length
    }, 0)
  }

  priceRole(role: ProductRoleType): number {
    const b_interval = { year: "annualTotal", month: "month" }[
      this.billingInterval
    ]

    return this.standardPlan.pricing[role][b_interval]
  }

  priceRoleTotal(role: ProductRoleType): number {
    const roleT = this.member_roles.find((m) => m.id === role)
    if (!roleT) return 0
    return this.priceRole(roleT.id) * roleT.count
  }

  changesMemberCount(role: ProductRoleType) {
    const index = this.member_roles.findIndex((m) => m.id === role)
    if (index === -1) return

    return this.signupCount(role) - this.subscription[`${role}_total`]
  }

  get totalSeats() {
    return SIGNUP_PRODUCT_ROLES.reduce((tot, role) => {
      return tot + this.subscription[`${role.id}_total`]
    }, 0)
  }

  get availableSeats() {
    return this.totalSeats - appStore.users.length
  }

  availableSeatsFor(role: ProductRoleType): number {
    return this.subscription[`${role}_total`] - this.membersCount(role)
  }

  setSignupDataToSubscription() {
    SIGNUP_PRODUCT_ROLES.forEach((role) => {
      const index = this.member_roles.findIndex((m) => m.id === role.id)
      if (index === -1) return

      this.member_roles[index].count =
        this.subscription[`${role.id}_total`] || role.min
    })
  }

  prepareFreeMembers() {
    this.member_roles.forEach((role) => {
      role.count = 0
    })
    appStore.users.forEach((u) => {
      const role = SUBSCRIPTION_ROLES_LIST.find((role) => role.id === u.role)
      if (!role) return

      const idx = this.member_roles.findIndex((m) => m.id === role.product_role)

      this.member_roles[idx].count++
    })
  }
}

export const signupStore = new SignupStore()
